import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useCurrentView } from '../hooks/useCurrentView';
import { onSignOut } from '../lib/signOutReset';

/**
 * Wallet — the user's Coin balance and history (economy epic, first slice).
 *
 * The server is the only source of truth: get_my_wallet() (SECURITY DEFINER)
 * claims any due grants (welcome bundle once; Free monthly grant per month)
 * and returns the balance; the transaction list is owner-scoped RLS. Clients
 * can never write the ledger. AI does NOT charge Coin yet — ai_action_prices
 * is display-only during beta.
 */

export interface WalletTransaction {
  id: string;
  amount_cp: number;
  // ⚠️ Hand-maintained mirror of the wallet_transactions.kind CHECK (mig 0189,
  // extended by 0229). Nothing type-checks this against the database — if a
  // migration adds a kind, add it here in the same commit.
  kind:
    | 'welcome_bundle'
    | 'monthly_grant'
    | 'topup'
    | 'ai_spend'
    | 'adjustment'
    | 'gift'
    | 'marketplace_buy'
    | 'marketplace_sale';
  note: string;
  created_at: string;
}

export interface AiPrice {
  key: string;
  label: string;
  price_cp: number;
}

export interface TopupPack {
  id: string;
  name: string;
  coin_cp: number;
  price_usd: number;
  discount_label: string;
  /** Apple IAP product id (mig 0232); null on web-only packs. */
  apple_product_id: string | null;
}

export interface UserPlan {
  plan_key: string;
  status: 'none' | 'active' | 'past_due' | 'canceled';
  current_period_end: string | null;
}

export interface PublicPlan {
  key: string;
  name: string;
  cadence: 'none' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
  price_usd: number;
  per_month_usd: number;
  discount_label: string;
  coin_grant_cp: number;
  welcome_bundle_cp: number;
  badge: string;
}

interface WalletState {
  balanceCp: number | null; // null until first load
  monthInCp: number;
  monthOutCp: number;
  transactions: WalletTransaction[];
  aiPrices: AiPrice[];
  packs: TopupPack[];
  /** Subscription state (user_plans, server-written only); null = Free. */
  plan: UserPlan | null;
  /** PUBLISHED premium plans (get_public_pricing) — empty until pricing goes live. */
  premiumPlans: PublicPlan[];
  /** One-shot: set when the user just returned from a successful checkout. */
  checkoutReturn: 'success' | 'cancelled' | null;
  clearCheckoutReturn: () => void;
  loading: boolean;
  initialized: boolean;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  priceFor: (key: string) => number | null;
  /** Back to empty — run on sign-out (lib/signOutReset). */
  clearStore: () => void;
}

const emptyWallet = () => ({
  balanceCp: null,
  monthInCp: 0,
  monthOutCp: 0,
  transactions: [] as WalletTransaction[],
  aiPrices: [] as AiPrice[],
  packs: [] as TopupPack[],
  plan: null,
  premiumPlans: [] as PublicPlan[],
  checkoutReturn: null,
  loading: false,
  initialized: false,
});

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A refresh notes it before its first await and
 * drops its answer when it has moved: the balance, history and plan read for the
 * account that signed out must not land in the next account's wallet.
 */
let epoch = 0;

export const useWalletStore = create<WalletState>((set, get) => ({
  ...emptyWallet(),

  clearCheckoutReturn: () => set({ checkoutReturn: null }),

  initialize: async () => {
    if (get().initialized) return;
    set({ initialized: true });

    // Returning from Stripe Checkout? (success_url/cancel_url land on
    // /?checkout=…). Strip the param, remember the outcome, and open the
    // wallet so the user sees their credit arrive. The webhook can lag the
    // redirect by a few seconds — refresh() again shortly after.
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'cancelled') {
      params.delete('checkout');
      params.delete('kind');
      const rest = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${rest ? `?${rest}` : ''}`,
      );
      set({ checkoutReturn: checkout });
      if (checkout === 'success') {
        // ⚠️⚠️ THE RETURN FROM A REAL PAYMENT. This must land on the balance the
        // user just topped up — it routed to Status + `status-open-tab: 'wallet'`
        // until the wallet became its own view (2026-08-12). Left unchanged, the
        // removal of that tab would have dropped a paying customer on Overview
        // with no confirmation and no way to see the coin they bought.
        useCurrentView.getState().setCurrentView('wallet');
        setTimeout(() => void get().refresh(), 4000);
        setTimeout(() => void get().refresh(), 12000);
      }
    }

    await get().refresh();
  },

  refresh: async () => {
    const at = epoch;
    set({ loading: true });
    try {
      /**
       * ⚠️⚠️ **EVERY USER-OWNED TABLE READ HERE MUST FILTER BY `user_id`
       * EXPLICITLY — RLS IS NOT THE FILTER** (found live 2026-08-08).
       *
       * `wallet_transactions` and `user_plans` each carry TWO permissive SELECT
       * policies (migrations 0189 / 0190): read-own, **and** `public.is_admin()`.
       * Postgres ORs permissive policies, so for an admin both tables return
       * EVERY user's rows — and an unfiltered query then renders a stranger's
       * data as the signed-in user's own.
       *
       * Measured on the owner's account before the fix: the history query below
       * returned 16 rows, **5 of them belonging to another user**, including
       * their monthly grant and two marketplace purchases, all listed in the
       * Wallet tab as if they were his.
       *
       * ⚠️ The BALANCE was never affected and must keep coming from
       * `get_my_wallet()` — a SECURITY DEFINER function that filters by
       * `auth.uid()` itself. That is exactly why this hid for so long: the
       * headline number stayed correct while the list beneath it did not.
       *
       * ⚠️⚠️ `user_plans` is the one that would have bitten hardest. It is read
       * with `.maybeSingle()` and DECIDES THE SUBSCRIPTION TIER, so once any
       * user has a plan row an admin would either inherit a stranger's plan or
       * get a multiple-rows error. It is empty today — this is a latent bug
       * fixed before it could fire, not one that was observed.
       *
       * `getSession()` reads local storage, so this costs no round-trip. A null
       * uid yields `.eq('user_id', null)` → no rows, which is the right answer
       * for a signed-out caller.
       */
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData?.session?.user?.id ?? null;

      const [wallet, txns, prices, packs, plan, publicPricing] = await Promise.all([
        supabase.rpc('get_my_wallet'),
        supabase
          .from('wallet_transactions')
          .select('id, amount_cp, kind, note, created_at')
          .eq('user_id', uid as string)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('ai_action_prices')
          .select('key, label, price_cp')
          .eq('active', true)
          .order('sort'),
        supabase
          .from('topup_packs')
          .select('id, name, coin_cp, price_usd, discount_label, apple_product_id')
          .eq('is_published', true)
          .order('sort'),
        // Server-written subscription state; no row (or pre-migration 404) = Free.
        // ⚠️⚠️ `.eq('user_id', …)` is load-bearing, not decoration — see the note
        // above. Without it an admin's `.maybeSingle()` reads whatever plan row
        // the table happens to hold.
        supabase
          .from('user_plans')
          .select('plan_key, status, current_period_end')
          .eq('user_id', uid as string)
          .maybeSingle(),
        // Published premium plans for the upgrade card (same source the
        // website pricing page reads).
        supabase.rpc('get_public_pricing'),
      ]);
      // Signed out while it read: the next account must not get this one's balance, history or plan.
      if (at !== epoch) return;
      const w = Array.isArray(wallet.data) ? wallet.data[0] : wallet.data;
      const pricing = publicPricing.data as { plans?: PublicPlan[] } | null;
      set({
        balanceCp: wallet.error ? get().balanceCp : Number(w?.balance_cp ?? 0),
        monthInCp: Number(w?.month_in_cp ?? 0),
        monthOutCp: Number(w?.month_out_cp ?? 0),
        transactions: (txns.data ?? []) as WalletTransaction[],
        aiPrices: (prices.data ?? []) as AiPrice[],
        packs: (packs.data ?? []) as TopupPack[],
        plan: (plan.data as UserPlan | null) ?? null,
        premiumPlans: (pricing?.plans ?? []).filter((p) => p.cadence !== 'none'),
      });
    } catch (err) {
      console.error('Wallet refresh failed:', err);
    } finally {
      if (at === epoch) set({ loading: false }); // a dropped refresh must not end the next account's
    }
  },

  priceFor: (key: string) => {
    const p = get().aiPrices.find((x) => x.key === key);
    return p ? p.price_cp : null;
  },

  /**
   * ⚠️⚠️ THE NEXT ACCOUNT LOADS ITS OWN WALLET. `initialize` runs once (`initialized`), and
   * a sign-out does not reload the page — so the next account to sign in on this device
   * kept the last one's balance, history and PLAN (its subscription tier) until something
   * happened to refresh it. Forgetting them also re-arms `initialize`.
   */
  clearStore: () => {
    epoch += 1;
    set(emptyWallet());
  },
}));

onSignOut(() => useWalletStore.getState().clearStore());
